// @flow

import React, { PureComponent, Fragment } from "react";
import { graphql } from "@apollo/client/react/hoc";
import { filter } from "graphql-anywhere";
import { get } from "lodash";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import type { Device } from "@tvg/types/Device";
import type { RaceProgram } from "@tvg/types/Race";
import type { UserOptedInPromos } from "@tvg/types/User";
import RaceInfo from "@tvg/atomic-ui/_templates/RaceInfo";
import RacePools from "@tvg/atomic-ui/_templates/RacePools";
import RaceWillPays from "@tvg/atomic-ui/_templates/RaceWillpays";
import RaceChanges from "@tvg/atomic-ui/_templates/RaceChanges";
import RaceProbables from "@tvg/atomic-ui/_templates/RaceProbables";
import AllRacesChanges, {
  type ChangesGTM
} from "@tvg/atomic-ui/_templates/AllRacesChanges";
import type { UnaryFn, NullaryFn } from "@tvg/types/Functional";
import { type TabKey } from "@tvg/atomic-ui/_templates/PoolsNInfo";

import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import { isOptedInPromoRace } from "../../utils";
import PoolsNInfoQuery from "./graphql/queries/PoolsNInfoQuery.graphql";
import ApolloOptions from "./graphql/options.graph";
// Fragments
import RacePoolsFragment from "./graphql/fragments/RacePools.graphql";
import PollsNInfoSubscription from "./graphql/subscriptions/PoolsNInfoSubscription.graphql";
import ChangesFragment from "./graphql/fragments/changes.graphql";
import WillPaysFragment from "./graphql/fragments/WillPays.graphql";
import ProbablesFragment from "./graphql/fragments/Probables.graphql";
import { Container, InfoNChangesContainer } from "./styled-components";

type Props = {
  currentRace: RaceProgram,
  tabSelected: TabKey,
  shouldRenderInline: boolean,
  device: Device,
  hasPromoBadge: boolean,
  optedInPromos: UserOptedInPromos,
  enableAllRaceChanges: boolean,
  hasWebSockets: boolean,
  subscribeToMore: UnaryFn<mixed, void>,
  wagerProfile: string,
  races: RaceProgram[]
};

type State = {
  hasSubscription: ?NullaryFn<void>
};

export class PoolsNInfoContainerComponent extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasSubscription: null
    };
  }

  componentDidUpdate(): void {
    if (
      this.props.subscribeToMore &&
      this.props.currentRace &&
      !this.state.hasSubscription
    ) {
      if (typeof this.state.hasSubscription === "function") {
        this.state.hasSubscription();
      }

      const tvgRaceIds = (this.props.races || []).map((race) =>
        get(race, "tvgRaceId")
      );

      const unsubscribe = this.props.subscribeToMore({
        document: PollsNInfoSubscription,
        variables: {
          wagerProfile: this.props.wagerProfile,
          tvgRaceIds,
          //  To delete sourcePage, product, brand after debugging UW-1703
          sourcePage: "PoolsNInfo",
          product: tvgConf().product,
          brand: tvgConf().brand
        }
      });

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasSubscription: () => unsubscribe });
    }
  }

  getPromo = () =>
    this.props.hasPromoBadge &&
    get(this.props.currentRace, "promo.isAboveTheLine") !== undefined
      ? {
          ...get(this.props.currentRace, "promo"),
          isOptedIn: isOptedInPromoRace(
            this.props.currentRace,
            this.props.optedInPromos
          )
        }
      : null;

  getTabToRender = () => {
    switch (this.props.tabSelected) {
      case "willpays": {
        const data = filter(WillPaysFragment, this.props);
        return (
          <RaceWillPays
            willpays={data.willPays}
            raceTypeCode={get(this.props, "currentRace.type.code")}
            raceNumber={get(this.props, "currentRace.raceNumber")}
            trackName={get(this.props, "currentRace.track.trackName")}
            handleGtmOnTabClick={this.handleGtmForTabClick}
          />
        );
      }
      case "probables": {
        const data = filter(ProbablesFragment, this.props);
        return (
          <RaceProbables
            probables={data.probables}
            raceTypeCode={get(this.props, "currentRace.type.code")}
            wagerTypeLimit={70}
            handleGtmOnTabClick={this.handleGtmForTabClick}
          />
        );
      }
      case "pools": {
        const pools = filter(RacePoolsFragment, this.props);
        return (
          <RacePools
            racePools={pools.racePools}
            runnersPools={pools.runnersPools}
            raceTypeCode={get(this.props, "currentRace.type.code")}
            wagerTypeLimit={70}
          />
        );
      }
      default: {
        const raceData = RaceHeaderUtils.createRaceDataForHeader(
          this.props.currentRace
        );
        const data = filter(ChangesFragment, this.props);

        const races = get(this.props, "racesChanges");
        const raceNumber = get(this.props, "currentRace.raceNumber");
        return (
          <InfoNChangesContainer>
            <RaceInfo
              highlightedDescription={get(
                this.props,
                "currentRace.highlighted.description"
              )}
              isGreyDarkText
              importanceLevel={RaceHeaderUtils.getImportanceLevel(
                get(this.props, "currentRace.highlighted.style")
              )}
              raceData={raceData}
              description={this.props.currentRace.description}
              hideRaceInfo={this.props.device && this.props.device !== "mobile"}
              promo={this.getPromo()}
            />
            {!get(this.props, "currentRace.isGreyhound", true) && (
              <Fragment>
                {data && data.changes && (
                  <RaceChanges
                    changes={data.changes}
                    raceTypeCode={get(this.props, "currentRace.type.code")}
                    raceNumber={raceNumber}
                  />
                )}
                {this.props.enableAllRaceChanges && races && (
                  <AllRacesChanges
                    races={races}
                    trackName={get(this.props.currentRace, "track.trackName")}
                    trackCode={get(this.props.currentRace, "track.trackCode")}
                    raceNumber={raceNumber}
                    triggerGTMEvent={this.triggerGTMEvent}
                  />
                )}
              </Fragment>
            )}
          </InfoNChangesContainer>
        );
      }
    }
  };

  handleGtmForTabClick = (section: string) => {
    mediator.base.dispatch({
      type: "POOLS_INFO:EXPAND_COLLAPSE",
      payload: {
        action: "select",
        section,
        raceNumber: get(this.props, "currentRace.raceNumber"),
        trackName: get(this.props, "currentRace.track.trackName")
      }
    });
  };

  triggerGTMEvent = (gtmParams: ChangesGTM): void => {
    mediator.base.dispatch({
      type: "POOLS_INFO:EXPAND_COLLAPSE",
      payload: {
        action: gtmParams.action,
        section: gtmParams.section,
        raceNumber: gtmParams.raceNumber,
        trackName: get(this.props.currentRace, "track.trackName")
      }
    });
  };

  render() {
    return (
      <Container autoHeight={!this.props.shouldRenderInline}>
        {this.getTabToRender()}
      </Container>
    );
  }
}

export default graphql(
  PoolsNInfoQuery,
  ApolloOptions
)(PoolsNInfoContainerComponent);
